import { useToast } from "../../../components/AlertToast/useToast";
import { useEffect } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomAlert } from "../../../components/Alert/CustomAlert";
import { resetAuthHeaderBlocked } from "../../../_services/authHeaderFallback";
// GEN-3167: quem sai de propósito não volta para a tela onde a sessão anterior estava.
import { clearReturnTo } from "../../../_services/returnTo";
import { logAuthDiag } from "../../../_services/authClient";
// GEN-4150: sessão SSO expirada renova pelo broker do cliente, não pela tela de senha.
import {
  decidirRenovacaoPeloSso,
  getSsoClient,
  irParaBroker,
  peekSsoReloginAttempt,
} from "../../../_services/ssoRelogin";

/**
 * GEN-3224: os motivos de saída viraram três (conta bloqueada, conta desativada, sessão
 * expirada). O motivo chega como marcador na query (`/logout?blocked=1`), lido com
 * `URLSearchParams` — o `includes()` cru de antes casava com qualquer substring da query.
 */
const LOGOUT_REASONS: [marker: string, descriptionHtml: string][] = [
  [
    "blocked",
    `<div><b>Conta bloqueada.</b></div>Sua conta foi bloqueada e o acesso ao sistema foi encerrado. Procure o administrador da sua empresa para desbloqueá-la.`,
  ],
  [
    "inactive",
    `<div><b>Conta desativada.</b></div>Sua conta foi desativada e o acesso ao sistema foi encerrado. Procure o administrador da sua empresa.`,
  ],
  [
    "expired",
    `<div><b>Sessão Expirada.</b></div>Sua sessão expirou no sistema, se deseja continuar utilizando, por favor, logue-se novamente.`,
  ],
];

export const getLogoutReasonHtml = (search: string): string | null => {
  const params = new URLSearchParams(search);
  return LOGOUT_REASONS.find(([marker]) => params.get(marker) === "1")?.[1] ?? null;
};

/**
 * GEN-3167 (deep-review) — a sessão expirar NÃO é sair do sistema.
 *
 * `/logout?expired=1` é para onde o produto manda quem estava usando a tela e teve a sessão
 * derrubada (o K2 faz isso em qualquer 401 e no 403 "Forbidden resource" do guard — GEN-4137 —, e
 * não é o único). É a MESMA pessoa na frente do
 * navegador, querendo voltar para onde estava — apagar o destino aqui fazia o login seguinte cair
 * no Dashboard, ou seja, matava justamente um dos dois casos que esta task existe para resolver.
 *
 * Nos outros motivos o destino é descartado: sair de propósito pode ser a troca de pessoa no mesmo
 * navegador, e conta bloqueada/desativada não vai voltar para lugar nenhum.
 */
export const deveManterDestino = (search: string): boolean => {
  const params = new URLSearchParams(search);
  // O estado da conta tem precedência sobre a expiração — a mesma regra do `getLogoutReasonHtml`.
  // Chegando os dois marcadores, quem manda é o bloqueio/desativação: o destino é descartado.
  if (params.get("blocked") === "1" || params.get("inactive") === "1") return false;
  return params.get("expired") === "1";
};

export const Logout = () => {
  // useCookies retorna [cookies, setCookie, removeCookie] — o remove é o TERCEIRO
  // elemento. A versão anterior desestruturava o setCookie como se fosse o remove,
  // e condicionava o navigate à remoção ter surtido efeito; qualquer falha na
  // limpeza deixava o usuário preso em /logout com a sessão viva.
  const [cookies, , removeCookie] = useCookies(["token"]);
  const navigate = useNavigate();
  const toast = useToast();
  const location = useLocation();

  useEffect(() => {
    localStorage.clear();
    // GEN-3199: a detecção do bloqueio do header vive em sessionStorage (fora do
    // clear acima). Zerar no logout evita que um falso-positivo (ex.: queda de
    // rede momentânea) mantenha o cliente sem header indefinidamente.
    resetAuthHeaderBlocked();

    if (cookies.token) {
      removeCookie("token", { path: "/" });
    }

    // GEN-3224: o motivo da saída não é mais só "sessão expirada". Conta bloqueada/desativada
    // (403 AUTH_USER_BLOCKED / AUTH_USER_INACTIVE) também cai aqui, e a mensagem tem que dizer
    // o motivo real — mandar "logue-se novamente" nesses casos só empurra o usuário para um
    // login que vai falhar de novo.
    //
    // A mensagem NÃO pode depender do cookie ainda existir: se outra aba já processou o logout
    // (ou o cookie foi gravado com atributos que o removeCookie não replicou), o usuário via
    // apenas o redirect silencioso para /login — sem saber que a conta está bloqueada.
    const descriptionHtml = getLogoutReasonHtml(location.search);

    const mostrarMotivo = () => {
      if (!descriptionHtml) return;
      toast.closeAll();
      toast({
        status: "error",
        duration: 5000,
        isClosable: true,
        containerStyle: {
          maxW: "800px",
        },
        render: ({ onClose }) => (
          <CustomAlert
            closableFunc={onClose}
            descriptionHtml={descriptionHtml}
            icon={true}
            status={"error"}
            variant={"alert"}
            type={"three"}
            className={"alert-instance"}
          />
        ),
      });
    };

    // Fallback: garante a remoção mesmo se o cookie tiver sido gravado com
    // atributos que o removeCookie não replique (path/domain divergentes).
    document.cookie = "token=; Max-Age=0; path=/";

    // GEN-3167: descarta um destino pendente — pode ser outra pessoa entrando neste navegador.
    // Menos quando a sessão apenas expirou: aí é a mesma pessoa, e o destino é o que a traz de
    // volta para a tela onde estava (ver `deveManterDestino`).
    const expirou = deveManterDestino(location.search);
    if (!expirou) clearReturnTo();

    /**
     * GEN-4150: quem entrou pelo broker de SSO (cookie `sso_client`) e teve a sessão EXPIRADA volta
     * ao broker em vez da tela de senha — com a sessão do IdP viva, a renovação é transparente, e o
     * `returnTo` guardado acima traz a pessoa de volta à tela onde estava. Só `expired=1` faz isso:
     * no logout deliberado o Entra re-autenticaria em silêncio e ninguém conseguiria sair; em
     * bloqueio/desativação o IdP devolveria a pessoa para a mesma negativa. A URL vem SEMPRE do
     * backend (allowlist), nunca do cookie. Ver `_services/ssoRelogin.ts`.
     */
    const sair = async () => {
      const ssoClient = getSsoClient();
      const decisao = decidirRenovacaoPeloSso({ expirou, ssoClient, lastAttemptAt: peekSsoReloginAttempt() });

      if (decisao === "renovar" && ssoClient) {
        // `irParaBroker` marca o cooldown ANTES do `await` e tem timeout curto (esta tela é em
        // branco, não pode ficar presa esperando o backend). Sem URL, segue o fluxo antigo abaixo.
        if (await irParaBroker(ssoClient, "sso-relogin:redirect")) return;
      } else if (decisao === "suprimido") {
        // Dois `expired=1` em menos de 60s nesta aba: o token que voltou do broker morreu de novo.
        // Não insistir — senão vira ping-pong infinito. A pessoa vê a tela de login com o botão de SSO.
        logAuthDiag("sso-relogin:suppressed", { ssoClient: ssoClient ?? undefined, reason: "cooldown" });
      }

      mostrarMotivo();
      // Sempre sai da tela de logout — não pode ficar condicionado ao estado do
      // cookie, senão /logout vira beco sem saída.
      navigate("/login", { replace: true });
    };

    void sair();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <></>;
};
